import React, { useState, useEffect } from "react";
import CheckBox from "components/FormElements/CheckBox";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { apiPut } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import { getGeneralSettings } from "redux/actions/content-flow";
import { toast } from "react-hot-toast";
import Clips from "./General/Clips";
import TextInput from "components/FormElements/TextInput";
import { packClasses } from "Utils";

export default function GeneralPanel(props) {
    const dispatch = useDispatch();
    const auth = useAuth();

    const [formValues, setFormValues] = useState({
        personality: false,
        agentAccess: true,
        localSave: false,
        editorCss: "",
        ffContentCache: false,
        ffNewSearchServer: false,
        plausibleUrl: false,
        clips: [],
        ffCleanPaste: false,
        ffAltTextGeneration: false,
        ffSortableAuthors: false,
    });
    const settings = useSelector((state) => state.contentFlow?.settings);

    const onSubmit = (values) => {
        apiPut("/v4/setting", auth.publication, {
            personality: values.personality ? true : false,
            localSave: values.localSave ? true : false,
            editorCss: values.editorCss ? values.editorCss : "",
            clips: values.clips ? values.clips : [],
            ffContentCache: values.ffContentCache ? true : false,
            plausibleUrl: values.plausibleUrl ? values.plausibleUrl : "",
            new_search_server: values.ffNewSearchServer ? true : false,
            ffCleanPaste: values.ffCleanPaste ? true : false,
            ffAltTextGeneration: values.ffAltTextGeneration ? true : false,
            ffSortableAuthors: values.ffSortableAuthors ? true : false,
        })
            .then((resp) => {
                dispatch(getGeneralSettings());
                toast.success("Settings updated");
            })
            .catch((e) => {
                toast.error("Error updating settings");
            });
    };

    useEffect(() => {
        setFormValues({
            personality: settings.settings?.personality || false,
            agentAccess: settings.settings?.agentAccess || true,
            localSave: settings.settings?.localSave || false,
            editorCss: settings.settings?.editorCss || "",
            clips: settings.settings?.clips || [],
            ffContentCache: settings.settings?.ffContentCache || false,
            ffNewSearchServer: settings.settings?.new_search_server || false,
            plausibleUrl: settings.settings?.plausibleUrl || "",
            ffCleanPaste: settings.settings?.ffCleanPaste || false,
            ffAltTextGeneration:
                settings.settings?.ffAltTextGeneration || false,
            ffSortableAuthors: settings.settings?.ffSortableAuthors || false,
        });
    }, [settings]);

    return (
        <Formik
            initialValues={formValues}
            enableReinitialize={true}
            onSubmit={onSubmit}
        >
            <Form className="flex h-full w-full flex-col">
                <div className="flex-grow overflow-auto p-4">
                    <div className="space-y-4">
                        <div className="text-md  text-gray-700">
                            General Settings
                        </div>
                        <div className="pl-4">
                            <CheckBox
                                label="Enable personality"
                                name="personality"
                            />
                            <div className="mt-1 text-xs text-gray-500">
                                Disable for 100% fewer dad jokes.
                            </div>
                        </div>
                        <div className="pl-4">
                            <CheckBox
                                label="Enable agent access"
                                name="agentAccess"
                                disabled={true}
                            />
                            <div className="mt-1 text-xs text-gray-500">
                                Allow SNworks support staff access to CEO. Agent
                                Access cannot be disabled during the beta.
                            </div>
                        </div>
                        <div className="text-md border-t border-gray-200 pt-4 text-gray-700">
                            Editor Settings
                        </div>
                        <div className="pl-4">
                            <CheckBox
                                label="Enable local save and recovery"
                                name="localSave"
                            />
                            <div className="mt-1 text-xs text-gray-500">
                                CEO will save your work locally while you write.
                            </div>
                        </div>
                        <div className="pl-4">
                            <TextInput
                                label="Site CSS"
                                name="editorCss"
                                placeholder="https://path/to/css"
                            />
                            <div className="mt-1 text-xs text-gray-500">
                                Import your site's CSS into the article editor.
                                To use a simple base CSS, you can use{" "}
                                <a
                                    href="https://static.getsnworks.com/tailwind/2.2.19/tailwind.min.css"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-green-600 underline"
                                >
                                    Tailwind 2
                                </a>
                                ,{" "}
                                <a
                                    href="https://static.getsnworks.com/bootstrap/4.6.2/bootstrap.min.css"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-green-600 underline"
                                >
                                    Bootstrap 4
                                </a>
                                , or{" "}
                                <a
                                    href="https://static.getsnworks.com/bootstrap/5.3.0/bootstrap.min.css"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-green-600 underline"
                                >
                                    Bootstrap 5
                                </a>
                                . Separate multiple CSS files with a comma.
                            </div>
                        </div>
                        <div className="pl-4">
                            <Clips />
                        </div>

                        <hr />
                        <div
                            className={packClasses(
                                "my-5 space-y-4 rounded bg-gray-50 p-4",
                                auth.user.is_snworks ? "" : "hidden"
                            )}
                        >
                            <div>
                                <CheckBox
                                    label="Feature Flag: Enable MeiliSearch content cache"
                                    name="ffContentCache"
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Set MeiliSearch as the content picker cache,
                                    instead of internal cache.
                                </div>
                            </div>
                            <div>
                                <CheckBox
                                    label="Feature Flag: Enable MeiliSearch"
                                    name="ffNewSearchServer"
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Use MeiliSearch search instead of Sphinx.
                                    This feature is intended to speed up CEO's
                                    search for large content repositories.
                                </div>
                            </div>
                            <div>
                                <CheckBox
                                    label="Feature Flag: Clean paste"
                                    name="ffCleanPaste"
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Clean up pasted content to remove
                                    unnecessary formatting.
                                </div>
                            </div>
                            <div>
                                <CheckBox
                                    label="Feature Flag: AI Alt Text"
                                    name="ffAltTextGeneration"
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Enable AI-generated alt text for images.
                                </div>
                            </div>
                            <div>
                                <CheckBox
                                    label="Feature Flag: Sortable Authors"
                                    name="ffSortableAuthors"
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    Enable sortable authors for content items.
                                </div>
                            </div>
                            <div>
                                <TextInput
                                    label="Embedded Analytics URL"
                                    name="plausibleUrl"
                                    placeholder="https://path/to/plausible"
                                    disabled={
                                        auth.user.is_snworks ? false : true
                                    }
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                    In beta, link provided by SNworks.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="w-full bg-gray-50 p-2 text-right">
                    <PrimaryButton label="Save Changes" type="submit" />
                </div>
            </Form>
        </Formik>
    );
}
